// Applying the profile's auth to an outbound request.
//
// Hosted and local authenticate differently — a cookie session on api.ato.run
// versus a bearer credential from a local Coordinator — but that is the ONLY
// difference, and it is not a reason for two client families. Clients hand
// their RequestInit through here and stay identical.

import type { ConnectionAuth } from "./types";
import { connectionProfile } from "./resolveProfile";
import { DEV_BYPASS_HEADER, devBypassToken } from "../../auth/devBypass";

/**
 * Merge headers that may be a `Headers`, an array of pairs, or a plain object,
 * into a plain object we can extend. Clients in this app use all three shapes.
 */
function headersToObject(headers: HeadersInit | undefined): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const out: Record<string, string> = {};
    headers.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return { ...(headers as Record<string, string>) };
}

/** Apply one auth mode to a request init. Exported for tests. */
export function applyAuth(auth: ConnectionAuth, init: RequestInit): RequestInit {
  if (auth.kind === "bearer") {
    const headers = headersToObject(init.headers);
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === "authorization") delete headers[key];
    }
    // Deliberately NOT `credentials: "include"`. A bearer connection is a
    // loopback Coordinator; sending ambient cookies to it would be both
    // useless and a way for cookie state to leak across realizations.
    return {
      ...init,
      credentials: "omit",
      headers: {
        ...headers,
        Authorization: `Bearer ${auth.token}`,
      },
    };
  }
  return { ...init, credentials: "include" };
}

/**
 * Authenticate a request according to the active connection profile.
 *
 * Hosted resolves to `credentials: "include"` — byte-for-byte what every client
 * wrote inline before. Local resolves to an `Authorization: Bearer` header.
 *
 * This makes the clients *capable* of carrying a bearer credential. It does not
 * mint one: no profile reports `bearer` yet, so every call here still takes the
 * cookie branch. Issuing a short-lived local credential is S6.
 *
 * Local-dev auth bypass (see `auth/devBypass.ts`): when armed, every client
 * additionally carries `X-Ato-Dev-Bypass` so the dev API resolves the scratch
 * identity. `devBypassToken()` is null in every build except an explicitly
 * armed `vite dev`, so this is a no-op everywhere else.
 */
export function withConnectionAuth(init: RequestInit = {}): RequestInit {
  const authed = applyAuth(connectionProfile().auth, init);
  // DEV-gated read: production bundles can never carry the header no matter
  // what env they are served with (devBypassToken alone is not sufficient —
  // see devBypass.ts).
  const token = import.meta.env.DEV ? devBypassToken() : null;
  if (token === null) return authed;
  return {
    ...authed,
    headers: {
      ...headersToObject(authed.headers),
      [DEV_BYPASS_HEADER]: token,
    },
  };
}
